import withRouter from "Components/Common/withRouter"
import ConsultantFields from "Components/organisms/forms/contractor-form/types"
import ConsultantForm from "Components/organisms/forms/contractor-form"
import FormPageTemplate from "Components/templates/form-page-template"
import ActionButton from "Components/atoms/buttons/action-button"
import AreYouSureButtons from "Components/molecule/are-you-sure-buttons"
import Modal from "Components/molecule/modal"
import { ConsultantValidation, PotentialConsultantValidation } from "data/consultants-form"
import { FormikHelpers, useFormik } from "formik"
import useManual from "hooks/useManual"
import { EMPLOYEE_TYPES, EmployeeType } from "models/employee-type"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { EmployeesSliceState, setEmployeeCount, setEmployees } from "slices/human-resources/employees/reducer"
import { getEmployeeFormFieldValues, submitConsultantForm, submitPotentialConsultantForm } from "slices/thunk"
import { HelpIcon } from "Components/atoms/icons"
import { toDepartmentOptions } from "utils/department-options"

const selectEmployees = createSelector(
	(state: any) => state.Employees,
	({
		jobTitles,
		officeLocations,
		managers,
		departments,
		status,
		employeeTypes,
		genders,
		employees,
		employeeCount
	}: EmployeesSliceState) => ({
		jobTitles,
		officeLocations,
		managers,
		departments,
		status,
		employeeTypes,
		genders,
		employees,
		employeeCount
	})
)


export const POTENTIAL_CONSULTANT_TYPE = "Potential Consultant"

const CreateConsultantPage = (props: any) => {
	document.title = "Create Consultant | IbexIQ";
	const { handleOpenManual } = useManual("human-resources/consultants");
	const dispatch: any = useDispatch();
	const [showAreYouSureModal, setShowAreYouSureModal] = useState<boolean>(false)
	const [employeeTypeId, setEmployeeTypeId] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const {
		jobTitles,
		officeLocations,
		managers,
		departments,
		status,
		employeeTypes,
		genders,
		employees,
		employeeCount,
	} = useSelector(selectEmployees);

	const isPotentialConsultant = useMemo(() => {
		const selected = employeeTypes.find(e => e.id === employeeTypeId)
		return selected?.name === POTENTIAL_CONSULTANT_TYPE
	}, [employeeTypeId, employeeTypes]);

	useEffect(() => {
		const initializePage = async () => {
			await dispatch(getEmployeeFormFieldValues())
			setIsLoading(false)
		}
		initializePage()
	}, [])


	const handleSubmitConsultantForm = (_vals: ConsultantFields, helpers: FormikHelpers<ConsultantFields>) => {
		helpers.setSubmitting(false)
		setShowAreYouSureModal(true)
	}

	const initialValues: ConsultantFields = useMemo(() => ({
		employeeCode: "",
		firstName: "",
		middleName: "",
		lastName: "",
		commonName: "",
		genderId: "",
		companyEmail: "",
		phoneNumber: "",
		jobTitleId: "",
		managerId: "",
		departmentId: "",
		officeLocationId: "",
		statusId: "",
		employeeTypeId: "",
		hireDate: "",
		terminationDate: "",
		contractEndDate: "",
	}), [status])

	const {
		values,
		errors,
		touched,
		handleSubmit,
		handleBlur,
		handleChange,
		isValid,
		setSubmitting,
		isSubmitting,
		handleReset
	} = useFormik({
		onSubmit: handleSubmitConsultantForm,
		enableReinitialize: true,
		initialValues: initialValues,
		validationSchema: isPotentialConsultant ? PotentialConsultantValidation : ConsultantValidation,
	})

	// We need to track when employee type changes so we can switch the validation.
	useEffect(() => {
		setEmployeeTypeId(values.employeeTypeId ?? null)
	}, [values.employeeTypeId])


	const handleBack = () => {
		props.router.navigate(-1)
		setShowAreYouSureModal(false)
	}

	const handleModalClose = () => setShowAreYouSureModal(false);


	const hanldeConfirmSubmitConsultantForm = useCallback(async (values: ConsultantFields, setSubmitting: (val: boolean) => any, reset: (e: any) => void) => {
		setSubmitting(true);
		const res = isPotentialConsultant ? await submitPotentialConsultantForm(values, () => reset({})) : await submitConsultantForm(values, () => reset({}));
		setSubmitting(false)

		if (!res) {
			handleModalClose()
		} else {
			res.employeeType = {} as EmployeeType;
			res.employeeType.name = isPotentialConsultant ? EMPLOYEE_TYPES.POTENTIAL_CONSULTANT : EMPLOYEE_TYPES.CONSULTANT
			dispatch(setEmployees([res, ...employees]))
			dispatch(setEmployeeCount(employeeCount + 1))
			handleBack()
		}
	}, [handleBack, handleModalClose, isPotentialConsultant, employees, employeeCount, dispatch])


	return <FormPageTemplate
		title="Human Resources"
		breadcrumbItem="Create Consultant"
		isLoading={isLoading}
		loadingTitle="Loading Form"
		loadingSubtitle="Fetching information…"
		actions={<ActionButton onClick={handleOpenManual}>
				Help <HelpIcon />
			</ActionButton>
		}
		form={<>
			<div>
				<h4>Create Consultant</h4>
				<h6>Please fill out the following fields and click submit.</h6>
			</div>
			<ConsultantForm
				edit={false}
				genders={genders.map(g => ({ value: g.id, label: g.description }))}
				jobTitles={jobTitles.map(jt => ({ value: jt.id, label: jt.description }))}
				managers={managers.map(e => ({ value: e.id ?? '', label: e.firstName + " " + e.lastName }))}
				departments={toDepartmentOptions(departments)}
				officeLocations={officeLocations.map(o => ({ value: o.id, label: o.description }))}
				status={status.map(s => ({ label: s.description, value: s.id }))}
				employeeTypes={employeeTypes.filter(e => e.name === EMPLOYEE_TYPES.CONSULTANT || e.name === POTENTIAL_CONSULTANT_TYPE).map(e => ({ label: e.name, value: e.id }))}
				values={values}
				errors={errors}
				touched={touched}
				isSubmitting={isSubmitting}
				isValid={isValid}
				potentialConsultant={isPotentialConsultant}
				handleChange={handleChange}
				handleBlur={handleBlur}
				handleSubmit={handleSubmit}
				handleCancel={handleBack}
			/>
		</>}
		modal={<Modal
			isOpen={showAreYouSureModal}
			onClose={handleModalClose}
			title={"Confirm"}
			subtitle={"Are you sure want to create consultant?"}
		>
			<AreYouSureButtons
				disable={isSubmitting}
				acceptButtonStyle={{ width: '150px' }}
				declineButtonStyle={{ width: '150px' }}
				handleAccept={() => hanldeConfirmSubmitConsultantForm(values, setSubmitting, handleReset)}
				handleDecline={handleModalClose}
				acceptText={isSubmitting ? "Submitting..." : "Submit"}
				declineText={"Cancel"}
			/>
		</Modal>}
	/>
}

export default withRouter(CreateConsultantPage);